import type { RootState } from '@/types/store'
import type { Store } from 'vuex'
import {
  getBusinessTimezoneName,
  resolveBusinessLocalNowIso,
  resolveTodayDateInBusinessTimezone,
} from './business-timezone'
import type { BusinessTimezone } from '../types/timezone'
import type {
  AgentCredentialHeaders,
  BusinessSetupAgentSessionState,
} from '../types/session'
import { resolveCountryGatewayOrigin } from '../../utils/resolve-country-gateway-origin'

export function resolveBooksyCoreBaseUrl (
  country: string,
  apiUrl?: string
): string {
  return `${resolveCountryGatewayOrigin(country, apiUrl)}/core/v2`
}

function buildBusinessTimezoneFields (
  business?: BusinessTimezone | null
): Pick<
  BusinessSetupAgentSessionState,
  'booksy_business_timezone' | 'booksy_business_timezone_offset' | 'booksy_today_date' | 'booksy_business_now'
> {
  const timezoneName = getBusinessTimezoneName(business ?? undefined)
  const timezoneOffset = business?.timezone

  return {
    ...(timezoneName ? { booksy_business_timezone: timezoneName } : {}),
    ...(timezoneOffset ? { booksy_business_timezone_offset: String(timezoneOffset) } : {}),
    booksy_today_date: resolveTodayDateInBusinessTimezone(business ?? undefined),
    booksy_business_now: resolveBusinessLocalNowIso(business ?? undefined),
  }
}

export function buildAgentCredentialHeaders (
  store: Store<RootState>
): AgentCredentialHeaders | null {
  const apiKey = store.state.api.apiKey
  const accessToken = store.state.identity.accessToken as string | undefined
  if (!apiKey || !accessToken) {
    return null
  }
  const country = store.state.app.country || 'us'
  return {
    'X-Booksy-Api-Key': apiKey,
    'X-Booksy-Access-Token': accessToken,
    'X-Booksy-Core-Base-Url': resolveBooksyCoreBaseUrl(country, store.state.api.apiUrl),
  }
}

export function buildBusinessSetupAgentSessionState (
  store: Store<RootState>
): BusinessSetupAgentSessionState {
  const country = store.state.app.country || 'us'
  const business = store.state.business.business
  const businessId = business?.id
  const primaryCategory = business?.primary_category_internal_name
  const currentStaffer = business?.current_staffer
  const user = store.state.identity.user
  const timezoneFields = buildBusinessTimezoneFields(business)
  const isSubscriptionBlocked = Boolean(store.getters['subscription/isApplicationBlocked'])
  const staffDiscountedPrice = store.getters['subscription/staffDiscountedPrice'] as string | undefined
  const staffPrice = staffDiscountedPrice && Number(staffDiscountedPrice) > 0
    ? String(staffDiscountedPrice)
    : undefined
  return {
    booksy_core_base_url: resolveBooksyCoreBaseUrl(country, store.state.api.apiUrl),
    ...timezoneFields,
    ...(store.state.app.locale ? { booksy_app_locale: store.state.app.locale } : {}),
    ...(country ? { booksy_app_country: country } : {}),
    ...(user?.id ? { booksy_user_id: user.id } : {}),
    ...(user?.email ? { booksy_user_email: user.email } : {}),
    ...(businessId
      ? {
        booksy_business_id: businessId,
        ...(business?.name ? { booksy_business_name: business.name } : {}),
        ...(business?.status ? { booksy_business_status: business.status } : {}),
        ...(primaryCategory ? { booksy_primary_business_category: primaryCategory } : {}),
        ...(isSubscriptionBlocked ? { booksy_subscription_blocked: true } : {}),
      }
      : {}),
    ...(staffPrice ? { booksy_staff_discounted_price_gross: staffPrice } : {}),
    ...(currentStaffer?.id ? { booksy_current_staffer_id: currentStaffer.id } : {}),
    ...(currentStaffer?.name ? { booksy_current_staffer_name: currentStaffer.name } : {}),
  }
}
